import React, { useContext, useMemo } from 'react';
import FullCalendar from '@fullcalendar/react';
import dayGridPlugin from '@fullcalendar/daygrid';
import { Chip, Box, useTheme, Tooltip, Typography } from '@mui/material';
import { isBefore, startOfToday, differenceInCalendarDays, format, addMinutes, addDays, parseISO } from 'date-fns';
import { darken } from '@mui/material/styles';
import { AppContext } from '../AppContext';
import { trackFeature } from '../utils/analytics';

export const taskEmojiMap = {
  sow: '🌱',
  blackout: '🕶️',
  lights: '💡',
  water: '💧',
  harvest: '✂️',
  delivery: '🚚',
};

export const taskColorMap = {
  sow: 'success',
  blackout: 'quaternary',
  lights: 'warning',
  water: 'primary',
  harvest: 'error',
  delivery: 'tertiary',
};

const DashCalendar = ({ tasks, onTaskClick, initialView = 'dayGridMonth', rollingWeek = false }) => {
  const theme = useTheme();
  // Get the user's preferred date format from context.
  const { userData, farmData } = useContext(AppContext);
  const defaultDateFormat = farmData?.settings?.dateFormat || 'dd-MM-yyyy';
  const weekStart = (farmData?.settings?.calendarWeekStart || 'monday').toLowerCase();
  const calendarStart = weekStart === 'monday' ? 1 : 0;

  const typePriority = {
    delivery: 1,
    harvest: 2,
    lights: 3,
    blackout: 4,
    sow: 5,
    water: 6,
  };

  // SafeGroup tasks by date and type
  const groupedTasks = {};
  tasks.forEach(task => {
    const key = `${task.date}_${task.type}`;
    if (!groupedTasks[key]) groupedTasks[key] = [];
    groupedTasks[key].push(task);
  });

  // Build FullCalendar events from grouped tasks
  const events = Object.entries(groupedTasks).map(([key, safegroup]) => {
    if (!safegroup.length) return null; // skip empty groups
    const [date, type] = key.split('_');
    const representative = safegroup[0];

    return {
      id: key,
      title: `${safegroup.length}x ${representative.label}`,
      start: date,
      end: addMinutes(parseISO(date), 1).toISOString(),
      priority: typePriority[type] ?? 99,
      extendedProps: {
        type: type,
        label: representative.label,
        name: representative.name,
        customColor: representative.color,
        emoji: representative.emoji,
        safegroup: safegroup, // <-- ensure it's here
      },
    };
  }).filter(Boolean); // filter out nulls

  const taskDateKeys = useMemo(() => {
    const keys = new Set();
    tasks.forEach((task) => {
      const raw = task?.date;
      if (!raw) return;
      const d = raw?.toDate ? raw.toDate() : raw instanceof Date ? raw : parseISO(raw);
      if (Number.isNaN(d?.getTime?.())) return;
      keys.add(format(d, 'yyyy-MM-dd'));
    });
    return keys;
  }, [tasks]);


  // Render each safegroup as one Chip
  const renderEventContent = (info) => {
    const props = info?.event?.extendedProps || {};
    const {
      type,
      label,
      name,
      customColor,
      emoji: overrideEmoji,
      safegroup,
    } = props;

    const safeGroup = Array.isArray(safegroup) ? safegroup : [];
    const shortFmt = defaultDateFormat.replace(/[-/]?yyyy/, '').trim();
    const emoji = overrideEmoji || taskEmojiMap[type] || '';
    const date = info.event.start;
    const harvestStartDate = props.harvestStartDate ? parseISO(props.harvestStartDate) : date;
    const suffix = type !== 'delivery' ? ` (${format(harvestStartDate, shortFmt)})` : '';
    const overdue = isBefore(date, startOfToday());
    const isToday = differenceInCalendarDays(date, startOfToday()) === 0;

    const count = safegroup.length > 1 ? ` x${safegroup.length}` : '';

    const text = safeGroup
      .map((task) => {
        const emoji = task.emoji || taskEmojiMap[task.type] || '';
        const shortFmt = defaultDateFormat.replace(/[-/]?yyyy/, '').trim();

        const baseDate = task.harvestStartDate
          ? parseISO(task.harvestStartDate)
          : parseISO(task.date); // fallback

        const suffix = task.type !== 'delivery'
          ? ` (${format(baseDate, shortFmt)})`
          : '';

        return `${emoji} ${task.label}${suffix}`;
      })
      .join('\n');



    const chipSx = {
      fontSize: '0.7rem',
      padding: '2px 6px', // 🌟 outer padding of the Chip
      height: 'auto',
      borderRadius: '12px',
      '& .MuiChip-label': {
        padding: '3px 0',      // 🌟 vertical padding inside the label
        whiteSpace: 'pre-line',
        textAlign: 'left',
      },
      ...(overdue
        ? { border: `2px solid ${theme.palette.error.main}` }
        : isToday
          ? { border: `2px solid ${theme.palette.secondary.main}` }
          : {}),
    };

    const mappedColor = taskColorMap[type];
    const baseBgColor = type === 'custom'
      ? (customColor || theme.palette.grey[700])
      : (
        type === 'lights'
          ? '#d37e3d'
          : type === 'blackout'
            ? (theme.palette[mappedColor]?.light || theme.palette[mappedColor]?.main || theme.palette.grey[700])
            : (theme.palette[mappedColor]?.dark || theme.palette[mappedColor]?.main || theme.palette.grey[700])
      );



    return (
      <Box sx={{ p: 0.25 }}>
        <Tooltip
          title={<Typography sx={{ fontSize: '0.8rem' }}>Type: {type.charAt(0).toUpperCase() + type.slice(1)}</Typography>}
          arrow
          placement="top"
        >
          <Chip
            size="small"
            label={text}
            variant="filled"
            sx={{
              backgroundColor: baseBgColor,
              color: theme.palette.getContrastText(baseBgColor),
              ...chipSx,
            }}
          />
        </Tooltip>
      </Box>
    );
  };



  const handleEventClick = (clickInfo) => {
    const props = clickInfo?.event?.extendedProps || {};
    const safegroup = Array.isArray(props.safegroup) ? props.safegroup : [];

    trackFeature('task_calendar_event_click', {
      count: safegroup.length,
      taskIds: safegroup.map(t => t.id),
      types: Array.from(new Set(safegroup.map(t => t.type))),
    });

    if (onTaskClick && safegroup.length) {
      onTaskClick(safegroup);
    }
  };




  const viewName = rollingWeek ? 'rollingWeek' : initialView;
  const calendarProps = rollingWeek ? {} : { firstDay: calendarStart };

  return (
    <Box
      sx={{
        /* Base */
/*         '& .fc': {
          fontFamily: theme.typography.fontFamily,
          backgroundColor: theme.palette.background.paper,
          color: theme.palette.text.primary,
        }, */

        /* Toolbar base */
        '& .fc-toolbar': {
          backgroundColor: theme.palette.primary.paper,
          padding: '3px 0',
          display: 'flex',
          flexWrap: 'nowrap',              // desktop default
          alignItems: 'center',
          gap: 0,
        },

        '& .fc-toolbar-title': {
          fontSize: { xs: '1.1rem', sm: '1.75rem' },
          fontWeight: 500,
          color: theme.palette.primary.text,
          whiteSpace: 'nowrap',
        },

        '& .fc-button': {
          backgroundColor: theme.palette.primary.main,
          border: 'none',
          color: theme.palette.primary.contrastText,
          fontWeight: 500,
          '&:hover': { backgroundColor: theme.palette.primary.dark },
        },
        '& .fc .fc-button.fc-button-active:focus': { outline: 'none !important', boxShadow: 'none !important' },
        '& .fc .fc-button.fc-button-active': { backgroundColor: theme.palette.primary.dark, color: theme.palette.primary.contrastText },
        '& .fc-button:focus, & .fc-button:active, & .fc-button:focus-visible': {
          outline: 'none !important',
          boxShadow: 'none !important',
          backgroundColor: `${theme.palette.primary.dark} !important`,
        },

        /* Header cells */
        '& .fc-col-header-cell': {
          border: `1px solid ${theme.palette.primary.dark}`,
          backgroundColor: theme.palette.primary.dark,
        },
        '& .fc-col-header-cell-cushion': {
          color: theme.palette.primary.contrastText,
          fontWeight: 600,
          fontSize: '0.75rem',
          opacity: 1,
          padding: '2px 0',
        },
        '& .dayHeaderLine': {
          display: 'block',
          lineHeight: 1.1,
        },

        /* Day numbers */
        '& .fc-daygrid-day-number': { color: theme.palette.text.primary, fontWeight: 500, fontSize: '0.8rem', opacity: 0.8 },

        /* Events */
        '& .fc-event': { border: 'none', padding: 0, background: 'transparent', cursor: 'pointer' },
        '& .fc-event.fc-event-selected, & .fc-event-selected, & .fc-event:focus, & .fc-event:focus-visible, & .fc-event:active': {
          outline: 'none !important',
          boxShadow: 'none !important',
          border: 'none !important',
          background: 'transparent !important',
        },
        '& .fc-event-selected:before, & .fc-event.fc-event-selected:before': {
          display: 'none !important',
        },
        '& .fc-event-selected .fc-event-main, & .fc-event.fc-event-selected .fc-event-main': {
          boxShadow: 'none !important',
          outline: 'none !important',
        },

        /* Theme vars */
        '& .fc-theme-standard': {
          '--fc-border-color': `${theme.palette.divider} !important`,
          '--fc-page-bg-color': `${theme.palette.background.alt} !important`,
          '--fc-today-bg-color': `${theme.palette.primary.darker} !important`,
        },

        '& .fc-daygrid-day.fc-day-empty': {
          backgroundColor: 'transparent',
          opacity: 0.55,
        },
        '& .fc-daygrid-day.fc-day-empty .fc-daygrid-day-frame': {
          borderColor: theme.palette.text.alt,
        },
        '& .fc-daygrid-day.fc-day-has-tasks': {
          backgroundColor: theme.palette.background.alt,
        },
        '& .fc-daygrid-day.fc-day-has-tasks .fc-daygrid-day-frame': {
          borderColor: theme.palette.background.alt,
        },
        '& .fc-daygrid-day.fc-day-today': {
          backgroundColor: `${darken(theme.palette.secondary.dark, 0.2)} !important`,
        },

        /* ===== Mobile layout (< sm) ===== */
        [theme.breakpoints.down('sm')]: {
          /* Wrap toolbar, create two rows */
          '& .fc-toolbar': {
            flexWrap: 'wrap',
            gap: 3,
          },

          /* First line: left chunk (prev/next/today) + center chunk (title) on one row */
          '& .fc-toolbar-chunk:first-of-type': {
            order: 1,
            alignItems: 'center',
            flex: '1 1 auto',
            gap: 6,
          },
          '& .fc-toolbar-chunk:nth-of-type(2)': {
            order: 1,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'flex-end',
            minWidth: 0,
          },

          '& .fc-toolbar-chunk:first-of-type .fc-button.fc-today-button': {
            padding: '4px 24px',
            fontSize: '1.05rem',
          },

          '& .fc-toolbar-title': {
            fontSize: { xs: '1.2rem', sm: '2rem', md: '2rem' }, // responsive sizes
            fontWeight: 600,
            lineHeight: 1.2,
            marginLeft: '8px',  // optional to separate from "today"
          },


          /* Second line: right chunk full-width, buttons spread and equal width */
          '& .fc-toolbar-chunk:last-of-type': {
            order: 2,
            flexBasis: '100%',
            display: 'flex',
            justifyContent: 'space-between',
          },
          /* Make the button group fill the row */
          '& .fc-toolbar-chunk:last-of-type .fc-button-group': {
            width: '100%',
            display: 'flex',
            gap: 0,
          },
          /* Make each view button flex equally */
          '& .fc-toolbar-chunk:last-of-type .fc-button-group .fc-button': {
            flex: 1,
            minWidth: 0,
            padding: '6px 0',
            fontSize: '0.9rem',
          },

          /* Tighten nav buttons + today a bit */
          '& .fc-toolbar .fc-button': {
            padding: '4px 10px',
          },
        },
      }}
    >
      <FullCalendar
        plugins={[dayGridPlugin]}
        initialView={viewName}
        initialDate={rollingWeek ? startOfToday() : undefined}
        views={rollingWeek ? {
          rollingWeek: {
            type: 'dayGrid',
            duration: { days: 7 },
            dateAlignment: 'day',
            fixedWeekCount: false,
            showNonCurrentDates: false,
          },
        } : undefined}
        headerToolbar={initialView === 'dayGridWeek'
          ? false
          : {
            left: 'prev,next today',
            center: 'title',
            right: 'dayGridMonth,dayGridWeek,dayGridDay',
          }}
        visibleRange={rollingWeek
          ? () => ({
            start: startOfToday(),
            end: addDays(startOfToday(), 7),
          })
          : undefined}
        dayHeaderFormat={{ weekday: 'short', month: 'numeric', day: 'numeric' }}
        dayHeaderContent={(arg) => {
          if (!arg.text) return { text: '' };
          const [first, ...rest] = arg.text.split(' ');
          if (!rest.length) return { text: arg.text };
          const second = rest.join(' ');
          return { html: `<span class="dayHeaderLine">${first}</span><span class="dayHeaderLine">${second}</span>` };
        }}
        dayCellClassNames={(arg) => {
          if (!rollingWeek) return [];
          const key = format(arg.date, 'yyyy-MM-dd');
          return taskDateKeys.has(key) ? ['fc-day-has-tasks'] : ['fc-day-empty'];
        }}
        events={events}
        {...calendarProps}
        eventContent={renderEventContent}
        eventClick={handleEventClick}
        slotEventOverlap={false}      // prevent side-by-side overlap
        eventDisplay="block"          // render each event as a full-width block
        height="auto"
      />
    </Box>
  );
};

export default DashCalendar;



